import {
  ITEM_IMAGE_POLICY,
  markItemImagePrepared,
  prepareItemImage,
} from '../../services/imageProcessing';
import { recordRapidCaptureMetric } from './rapidCaptureMetrics';

export const RAPID_CAPTURE_MAX_DIMENSION = ITEM_IMAGE_POLICY.maxSide;
export const RAPID_CAPTURE_MAX_BYTES = ITEM_IMAGE_POLICY.maxBytes;

const encodeJpeg = (canvas: HTMLCanvasElement): Promise<Blob> =>
  new Promise((resolve, reject) =>
    canvas.toBlob(
      (blob) =>
        blob
          ? resolve(blob)
          : reject(new Error('The camera frame could not be encoded.')),
      ITEM_IMAGE_POLICY.mimeType,
      ITEM_IMAGE_POLICY.initialQuality,
    ),
  );

/** Capture the centered square shown by the square, cover-fit camera preview.
 * Legacy display dimensions are accepted but cannot change the output aspect.
 * `deviceRoll` (degrees counter-clockwise, multiples of 90) uprights frames
 * taken while the phone is turned but the display orientation is locked.
 *
 * The frame is scaled to the policy size while drawing and JPEG-encoded once.
 * Only if that exceeds the byte limit does it fall back to the shared
 * processor's quality/size loop.
 */
export const captureVideoFrame = async (
  video: HTMLVideoElement,
  mirrored = false,
  _boxWidth = 0,
  _boxHeight = 0,
  deviceRoll = 0,
): Promise<File> => {
  const startedAt = performance.now();
  const { videoWidth: width, videoHeight: height } = video;
  if (!width || !height) throw new Error('The camera is not ready yet.');
  const size = Math.min(width, height);
  const side = Math.min(size, ITEM_IMAGE_POLICY.maxSide);
  const canvas = document.createElement('canvas');
  canvas.width = side;
  canvas.height = side;
  const context = canvas.getContext('2d');
  if (!context) throw new Error('Image capture is unavailable.');
  context.fillStyle = ITEM_IMAGE_POLICY.background;
  context.fillRect(0, 0, side, side);
  if (deviceRoll) {
    // The phone is turned counter-clockwise by deviceRoll relative to the
    // display, so turn the frame clockwise-negative to put the ground down.
    context.translate(side / 2, side / 2);
    context.rotate((-deviceRoll * Math.PI) / 180);
    context.translate(-side / 2, -side / 2);
  }
  if (mirrored) {
    context.translate(side, 0);
    context.scale(-1, 1);
  }
  context.drawImage(
    video,
    (width - size) / 2,
    (height - size) / 2,
    size,
    size,
    0,
    0,
    side,
    side,
  );
  const blob = await encodeJpeg(canvas);
  if (blob.size <= ITEM_IMAGE_POLICY.maxBytes) {
    const file = markItemImagePrepared(
      new File([blob], 'item-photo.jpg', { type: ITEM_IMAGE_POLICY.mimeType }),
    );
    recordRapidCaptureMetric('frame-capture', performance.now() - startedAt, {
      side,
      bytes: file.size,
      fallback: false,
    });
    return file;
  }
  const { file } = await prepareItemImage(blob, {
    crop: { x: 0, y: 0, size: side },
  });
  recordRapidCaptureMetric('frame-capture', performance.now() - startedAt, {
    side,
    bytes: file.size,
    fallback: true,
  });
  return file;
};
